import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { publicApi } from '../utils/api';
import { buildToolGoUrl } from '../utils/affiliateLinks';

const StackSharePage = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [stackDoc, setStackDoc] = useState(null);

  useEffect(() => {
    const loadStack = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await publicApi.getSharedStack(slug);
        setStackDoc(response.data);
      } catch (err) {
        setError(err?.response?.data?.detail || 'Unable to load shared stack');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadStack();
    }
  }, [slug]);

  const stack = stackDoc?.stack_payload || null;
  const publicStackItems = stackDoc?.items || [];

  const launchPublicStack = () => {
    const links = publicStackItems.map((item) => buildToolGoUrl(item)).filter((value) => value && value !== '#');
    links.forEach((link, index) => {
      setTimeout(() => window.open(link, '_blank', 'noopener,noreferrer'), index * 700);
    });
  };

  return (
    <div className="min-h-screen pt-24 page-bg-shell page-bg-code" data-testid="stack-share-page">
      <Helmet>
        <title>Shared AI Stack | Wowrolla</title>
      </Helmet>

      <section className="container mx-auto px-6 pb-16">
        <Link to="/my-stack/build" className="inline-flex items-center mb-6 transition-colors" style={{ color: 'var(--text-secondary)' }}>
          <ArrowLeft size={16} className="mr-2" />
          Build Your Own Stack
        </Link>

        <div className="terminal-grid p-6">
          {loading && (
            <p style={{ color: 'var(--text-secondary)' }}>Loading shared stack...</p>
          )}

          {!loading && error && (
            <p style={{ color: '#ff7777' }}>{error}</p>
          )}

          {!loading && !error && stack && (
            <div className="space-y-5">
              <div>
                <h1 className="text-3xl font-black" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>
                  {stackDoc?.stack_name || 'Shared Stack'}
                </h1>
                <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Role: {stack.role} - Company Size: {stack.company_size} - Budget: ${stack.budget_monthly}/mo
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(stack.recommendations || []).map((item) => (
                  <div key={`${item.capability}-${item.tool_id}`} className="p-4 border" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                    <p className="text-xs uppercase mb-1" style={{ color: 'var(--text-muted)' }}>{item.capability_label}</p>
                    <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{item.tool_name}</h3>
                    <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>{item.tool_tagline}</p>
                    <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
                      <strong style={{ color: 'var(--text-primary)' }}>Why:</strong> {item.why_selected}
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <Link to={`/tools/${item.tool_slug}`} style={{ color: 'var(--accent-primary)' }}>View</Link>
                      {buildToolGoUrl({ slug: item.tool_slug }) !== '#' && (
                        <a href={buildToolGoUrl({ slug: item.tool_slug })} target="_blank" rel="sponsored nofollow noopener noreferrer" style={{ color: 'var(--text-secondary)' }}>
                          Launch <ExternalLink size={14} className="inline" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div className="p-3 border" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                  <p style={{ color: 'var(--text-muted)' }}>Monthly Cost</p>
                  <p style={{ color: 'var(--text-primary)' }}>${stack.metrics?.total_monthly_cost ?? 0}</p>
                </div>
                <div className="p-3 border" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                  <p style={{ color: 'var(--text-muted)' }}>Hours Saved</p>
                  <p style={{ color: 'var(--text-primary)' }}>{stack.metrics?.estimated_hours_saved ?? 0}</p>
                </div>
                <div className="p-3 border" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                  <p style={{ color: 'var(--text-muted)' }}>Monthly Value</p>
                  <p style={{ color: 'var(--text-primary)' }}>${stack.metrics?.estimated_monthly_value ?? 0}</p>
                </div>
                <div className="p-3 border" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                  <p style={{ color: 'var(--text-muted)' }}>ROI</p>
                  <p style={{ color: 'var(--text-primary)' }}>{stack.metrics?.roi_percent ?? 0}%</p>
                </div>
              </div>

              <div className="p-4 border" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>Integration Notes</h3>
                <ul className="space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {(stack.integration_notes || []).map((note, index) => (
                    <li key={index}>{note.from}{' -> '}{note.to}: {note.note}</li>
                  ))}
                </ul>
                <pre className="mt-3 p-3 text-xs overflow-x-auto border" style={{ borderColor: 'rgba(255,255,255,0.12)', color: 'var(--text-muted)' }}>
                  {stack.diagram}
                </pre>
              </div>
            </div>
          )}

          {!loading && !error && !stack && publicStackItems.length > 0 && (
            <div className="space-y-5">
              <div>
                <h1 className="text-3xl font-black" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>
                  @{stackDoc?.username}'s Stack
                </h1>
                <p className="mt-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {stackDoc?.built_by || `Built by a ${stackDoc?.role_profile || 'operator'} at a ${stackDoc?.company_size || 'team'} company`}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {publicStackItems.map((item) => (
                  <div key={item.tool_id} className="p-4 border" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                    <h3 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>{item.name}</h3>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.category_name}</p>
                    <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>${Number(item.monthly_cost || 0).toFixed(2)} / month</p>
                    <div className="mt-3 flex items-center gap-2">
                      <Link to={`/tools/${item.slug}`} style={{ color: 'var(--accent-primary)' }}>View</Link>
                      {buildToolGoUrl(item) !== '#' && (
                        <a href={buildToolGoUrl(item)} target="_blank" rel="sponsored nofollow noopener noreferrer" style={{ color: 'var(--text-secondary)' }}>
                          Launch <ExternalLink size={14} className="inline" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between p-4 border" style={{ borderColor: 'rgba(255,255,255,0.15)' }}>
                <div style={{ color: 'var(--text-secondary)' }}>
                  Total Cost: <strong style={{ color: 'var(--text-primary)' }}>${Number(stackDoc?.total_monthly_cost || 0).toFixed(2)} / month</strong>
                </div>
                <button onClick={launchPublicStack} className="px-4 py-2 border" style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--accent-primary)' }}>
                  Copy this stack
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default StackSharePage;

